import classNames from 'classnames';
import { CSSProperties, FC, KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import { MoreHorizontalIcon as MoreSvg } from '../../libs/corporate/icons/outlined/action/MainCollection';
import { MultiplyIcon as ClearSvg } from '../../libs/corporate/icons/outlined/suggested/SymbolCollection';
import renderGlobalAlert from '../../system/hooks/useGlobalAlert';
import styles from './dictionaryInput.module.scss';
import BaseIconButton from '../buttons/iconButtons/BaseIconButton';
import Tooltip from '../tooltips/Tooltip';
import { useTranslation } from 'react-i18next';
import { useBarcodeReaderContext } from '../../system/providers/barcodeReaderGlobal';
import { PopoverClue } from '../popoverClue/PopoverClue';

type Variant = 'normal' | 'red' | 'green' | 'disabled' | 'required';

let draggedDictionaryInputValue: string | null = null;

interface IDictionaryInputProps {
  variant?: Variant;
  label?: string;
  labelClassName?: string;
  inputId?: string;
  placeholder?: string;
  value?: string;
  clues?: string[];
  disabled?: boolean;
  barcodeScan?: boolean;
  hiddenMoreButton?: boolean;
  hiddenClearButton?: boolean;
  disabledInput?: boolean;
  className?: string;
  inline?: boolean;
  error?: string;
  onChange?: (searchText?: string) => void;
  onMoreClick?: (value?: string) => void;
  onEnterClick?: (value?: string) => void;
  //isEnterIsolation?: boolean;
  onClearClick?: () => void;
  onFocus?: () => void;
  style?: CSSProperties;
  change?: IChangeData;
  numereticInput?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<any>;
  tabIndex?: number;
  'data-field-id'?: string;
}

const DictionaryInput: FC<IDictionaryInputProps> = (props) => {

  const { t } = useTranslation();
  const barcodeReaderContext = useBarcodeReaderContext();

  function getClassNames() {
    const variant = props.disabled === true ? 'disabled' : props.variant;

    if (props.inline) {
      switch (variant) {
        case undefined:
        case 'normal':
          return styles.dictNormalInline;
        case 'required':
          return styles.dictRequiredInline;
        case 'red':
          return styles.dictRedInline;
        case 'green':
          return styles.dictGreenInline;
        case 'disabled':
          return styles.dictDisabledInline;
      }
    } else {
      switch (variant) {
        case undefined:
        case 'normal':
          return styles.dictNormal;
        case 'required':
          return styles.dictRequired;
        case 'red':
          return styles.dictRed;
        case 'green':
          return styles.dictGreen;
        case 'disabled':
          return styles.dictDisabled;
      }
    }
  }

  const [value, setValue] = useState<string>('');
  const draggedValueRef = useRef<string | null>(null);

  const updateValue = (nextValue: string): void => {
    setValue(nextValue);
    props.onChange?.(nextValue);
  }

  const getDroppedValue = (e: React.DragEvent): string => {
    return e.dataTransfer.getData('text/plain') || draggedDictionaryInputValue || '';
  }

  const handleDragOver = (e: React.DragEvent): void => {
    if (draggedDictionaryInputValue !== null || e.dataTransfer.types.includes('text/plain')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }

  const handleDrop = (e: React.DragEvent): void => {
    const droppedValue = getDroppedValue(e);

    if (!droppedValue) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    updateValue(droppedValue);
  }

  const onCanChange = (): boolean => {
    if (props.change?.isCanChangeData === false) {
      renderGlobalAlert({
        variant: "warning",
        statusCode: 500,
        title: props.change.message,
      })
      return false
    }
    return true
  }

  useEffect(() => {
    setValue(props.value ?? '');
  }, [props.value]);

  useEffect(() => {
    props.autoFocus && dictionaryInput.current?.focus();
  }, [props.autoFocus]);

  //#barcode // scaning

  const internalRef = useRef<HTMLInputElement>(null);
  // Используем переданный ref или внутренний
  const dictionaryInput = props.inputRef || internalRef;

  useEffect(() => {
    if (props.barcodeScan && barcodeReaderContext.barcodeScanFlag === true) {
      const scannedBarcode = barcodeReaderContext.barcode;

      setTimeout(() => {
        dictionaryInput.current?.focus();
        setValue(scannedBarcode);
        props.onChange?.(scannedBarcode);
        
        const submitTimer = setTimeout(() => {
          if (props.onEnterClick) {
            props.onEnterClick(scannedBarcode);
          } 
        }, 150);
        
        return () => clearTimeout(submitTimer);
      }, 50);
    }
  }, [barcodeReaderContext.barcodeScanFlag]);

  return (
    <div
      style={props.style}
      className={
        props.error
          ? classNames(props.className, getClassNames(), styles.dictRed)
          : classNames(props.className, getClassNames(), props.required)
      }
    >
      {props.label && (
        <label className={props.labelClassName} htmlFor={props.inputId}>
          {props.required ? <b>{props.label} *</b> : props.label}
        </label>
      )}
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={dictionaryInput}
          id={props.inputId}
          disabled={props.disabledInput ?? props.disabled}
          readOnly={props.numereticInput ? true : false}
          name={props.inputId}
          placeholder={props.placeholder}
          autoComplete="off"
          value={value}
          tabIndex={props.tabIndex}
          data-field-id={props['data-field-id']}
          onChange={(e) => {
            updateValue(e.currentTarget.value);
          }}
          onDragStart={(e) => {
            draggedValueRef.current = value;
            draggedDictionaryInputValue = value;
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('text/plain', value);
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragEnd={() => {
            if (draggedValueRef.current !== null && value !== draggedValueRef.current) {
              updateValue(draggedValueRef.current);
            }

            draggedValueRef.current = null;
            draggedDictionaryInputValue = null;
          }}
          onKeyDown={(eKeyPress) => {
            props?.onKeyDown && props?.onKeyDown(eKeyPress)
            switch (eKeyPress.key) {
              case 'Enter':
                if (props.onEnterClick)
                  props.onEnterClick(value);
                else
                  props.onMoreClick?.(value);
                break;
              case 'Escape':
                eKeyPress.currentTarget.blur();
                break
              default:
                break;
            }
          }}
          onFocus={(e) => {
            props.onFocus?.();
          }}
          onBlur={() => {

          }}
        />
        {props.error && <span className={styles.errorMessage}>{props.error}</span>}

        {!props.hiddenMoreButton && (
          <div className={styles.dictionary}>
            <Tooltip title={t("general.open")}>
              <BaseIconButton
                tabIndex={-1}
                sizeVariant={'mini'}
                onClick={(e) => {
                  if (!onCanChange()) {
                    return
                  }
                  e.preventDefault();
                  props.onMoreClick?.(value);
                }}
                {...props}
              >
                <MoreSvg />
              </BaseIconButton>
            </Tooltip>
          </div>
        )}
        {!props.hiddenClearButton && (
          <div className={styles.dictionary}>
            <Tooltip title={t("general.clear")}>
              <BaseIconButton
                tabIndex={-1}
                sizeVariant={'mini'}
                onClick={(e) => {
                  if (!onCanChange()) {
                    return
                  }
                  e.preventDefault();
                  props.onClearClick?.();
                  setValue('');
                }}
                {...props}
              >
                <ClearSvg />
              </BaseIconButton>
            </Tooltip>
          </div>
        )}
      </div>
      {
        props.clues && (
          <div className={styles.popoverClue}>
            <PopoverClue
              clues={props.clues}
            />
          </div>
        )
      }
    </div>
  );
};
export { DictionaryInput };
